import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, 
  Users, 
  Trophy, 
  Plus, 
  ArrowUpRight, 
  ArrowDownRight,
  LogOut,
  Download,
  QrCode,
  Layers,
  Settings,
  Bell,
  Search,
  Zap,
  Shield,
  Gift,
  Star,
  ChevronLeft,
  ChevronRight,
  Eye
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { userService } from '../../services/userService';
import { QRCodeCanvas } from 'qrcode.react';
import { showAlert } from '../../utils/alert';

const Dashboard = () => {
  const [shop, setShop] = useState(null);
  const [scratches, setScratches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customBaseUrl, setCustomBaseUrl] = useState(window.location.origin);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [chartMode, setChartMode] = useState('daily'); // 'daily' or 'weekly'
  const navigate = useNavigate();

  useEffect(() => {
    const fetchShopData = async () => {
      try {
        const currentShop = await authService.getCurrentShop();
        if (currentShop) {
          setShop(currentShop);
          const history = await userService.getShopScratches(currentShop.id);
          setScratches(history);
        }
      } catch (error) {
        console.error("Error fetching shop:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchShopData();
  }, []);

  useEffect(() => {
    if (shop?.endDate) {
      const today = new Date();
      const end = new Date(shop.endDate);
      
      today.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      
      const diffTime = end.getTime() - today.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        showAlert('Subscription Notice', 'Your subscription is expiring today', 'warning');
      } else if (diffDays > 0 && diffDays <= 2) {
        showAlert('Subscription Notice', `Your subscription is expiring in ${diffDays} days`, 'warning');
      } else if (diffDays < 0) {
        showAlert('Subscription Expired', 'Your subscription has expired. Access restricted.', 'error')
          .then(() => handleLogout());
      }
    }
  }, [shop]);

  // Calculate dynamic stats from scratches history
  const totalInteractions = scratches.length;
  const uniqueUsers = new Set(scratches.map(s => s.userPhone)).size;
  const rewardsClaimed = scratches.length; // Each scratch is a claimed reward in this sequence

  // Generate chart data based on hourly distribution
  const getDailyDistribution = () => {
    const hours = new Array(12).fill(0); // 12 segments for the chart
    scratches.forEach(s => {
      if (s.timestamp) {
        const date = s.timestamp.toDate();
        // Only count today's interactions for daily
        const today = new Date();
        if (date.toDateString() === today.toDateString()) {
          const hour = date.getHours();
          const segment = Math.floor(hour / 2); // 2-hour segments
          if (segment < 12) hours[segment]++;
        }
      }
    });
    const maxVal = Math.max(...hours, 1);
    return hours.map(v => (v / maxVal) * 80 + 10);
  };

  const getWeeklyDistribution = () => {
    const days = new Array(7).fill(0);
    const today = new Date();
    
    scratches.forEach(s => {
      if (s.timestamp) {
        const date = s.timestamp.toDate();
        const diffTime = Math.abs(today - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays <= 7) {
          const dayIdx = (date.getDay() + (7 - today.getDay()) + 6) % 7; // Relative to today
          days[dayIdx]++;
        }
      }
    });
    const maxVal = Math.max(...days, 1);
    return days.map(v => (v / maxVal) * 80 + 10);
  };

  const chartData = scratches.length > 0 
    ? (chartMode === 'daily' ? getDailyDistribution() : getWeeklyDistribution())
    : new Array(chartMode === 'daily' ? 12 : 7).fill(20);

  const handleLogout = async () => {
    await authService.logout();
    navigate('/shopowner');
  };

  if (loading) {
    return <div className="min-h-screen bg-[#fdfdfd] flex items-center justify-center text-black font-serif italic tracking-widest">Entering Command Center...</div>;
  }

  return (
    <div className="min-h-screen bg-[#fdfdfd] flex text-[#1a1a1a] relative">
      <div className="absolute inset-0 luxury-grid-bg pointer-events-none" />

      {/* Mini Sidebar */}
      <aside className="w-24 bg-white border-r border-black/[0.05] flex flex-col items-center py-10 gap-10 shrink-0 relative z-10">
        <div className="w-12 h-12 bg-black rounded-2xl flex items-center justify-center text-[#d4af37] shadow-xl">
          <Layers size={24} />
        </div>
        
        <nav className="flex flex-col gap-6">
          <SidebarIcon icon={<BarChart3 size={20} />} active />
          <SidebarIcon icon={<Settings size={20} />} onClick={() => navigate('/admin/settings')} />
          <SidebarIcon icon={<Shield size={20} />} />
        </nav>

        <div className="mt-auto">
          <button 
            onClick={handleLogout}
            className="w-12 h-12 rounded-2xl bg-rose-500/5 text-rose-500 flex items-center justify-center hover:bg-rose-500 hover:text-white transition-all"
          >
            <LogOut size={20} />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 h-screen overflow-y-auto custom-scrollbar p-12 relative z-10">
        {/* Header */}
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-black text-[10px] font-black uppercase tracking-[0.4em] px-3 py-1 rounded-full bg-black/5">Merchant Command</span>
              <span className="text-[#ccc] text-[10px] font-black uppercase tracking-[0.4em]">•</span>
              <span className="text-[#999] text-[10px] font-black uppercase tracking-[0.4em]">{shop?.plan || 'Premium'} Tier</span>
            </div>
            <h1 className="text-4xl font-serif text-black tracking-tight">{shop?.shopName}</h1>
            <p className="text-[#666] text-[10px] font-bold uppercase tracking-[0.3em] mt-3">Governance Hub & Performance Analytics</p>
          </div>
          
          <div className="flex items-center gap-6">
            <div className="hidden lg:flex items-center gap-2 px-6 py-4 rounded-2xl bg-white border border-black/[0.05] shadow-sm">
              <Zap size={16} className="text-[#d4af37]" />
              <div className="flex flex-col">
                <span className="text-[9px] font-black text-[#999] uppercase tracking-widest">Node Status</span>
                <span className="text-[11px] font-bold text-black tracking-tight">Active & Optimized</span>
              </div>
            </div>

            <button 
              onClick={() => navigate('/admin/create-campaign')}
              className="luxury-button flex items-center gap-3 !py-4"
            >
              <Plus size={16} />
              Architect New Offer
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Main Analytics Area */}
          <div className="lg:col-span-8 space-y-10">
            {/* Real Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <StatCard title="Total Interaction" value={totalInteractions} change={`+${totalInteractions}`} isUp={true} icon={<QrCode size={22} />} />
            <StatCard title="Active Audience" value={uniqueUsers} change={`+${uniqueUsers}`} isUp={true} icon={<Users size={22} />} />
            <StatCard title="Rewards Dispersed" value={rewardsClaimed} change={`+${rewardsClaimed}`} isUp={true} icon={<Trophy size={22} />} />
            </div>

            {/* Performance Overview */}
            <div className="luxury-card p-10">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h3 className="text-xl font-serif text-black tracking-tight">Customer Engagement</h3>
                  <p className="text-[#999] text-[9px] font-bold uppercase tracking-widest mt-1">Live Interaction Metrics</p>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => setChartMode('daily')}
                    className={`text-[10px] font-bold px-4 py-2 rounded-xl transition-all ${chartMode === 'daily' ? 'text-black bg-black/5' : 'text-[#999] hover:text-black'}`}
                  >
                    Daily
                  </button>
                  <button 
                    onClick={() => setChartMode('weekly')}
                    className={`text-[10px] font-bold px-4 py-2 rounded-xl transition-all ${chartMode === 'weekly' ? 'text-black bg-black/5' : 'text-[#999] hover:text-black'}`}
                  >
                    Weekly
                  </button>
                </div>
              </div>

              {/* Simple Chart Visualization */}
              <div className="h-56 flex items-end gap-3 justify-between">
                {chartData.map((h, i) => (
                  <div key={i} className="flex-1 bg-black/[0.03] rounded-full relative group overflow-hidden">
                    <motion.div 
                      initial={{ height: 0 }}
                      animate={{ height: `${h}%` }}
                      transition={{ delay: i * 0.05, duration: 1 }}
                      className="absolute bottom-0 left-0 right-0 bg-black/10 group-hover:bg-[#d4af37]/40 transition-all"
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-6 text-[8px] font-bold text-[#999] uppercase tracking-tighter">
                {chartMode === 'daily' ? (
                  <><span>00:00</span><span>04:00</span><span>08:00</span><span>12:00</span><span>16:00</span><span>20:00</span><span>23:59</span></>
                ) : (
                  <><span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span></>
                )}
              </div>
            </div>

            {/* Campaigns Overview */}
            <div className="space-y-8">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-serif text-black tracking-tight">Reward Protocols</h3>
                  <p className="text-[#999] text-[9px] font-bold uppercase tracking-widest mt-1">Manage individual QR nodes</p>
                </div>
                <button 
                  onClick={() => navigate('/admin/create-campaign')}
                  className="luxury-button flex items-center gap-3 !py-4"
                >
                  <Plus size={16} /> Architect New Node
                </button>
              </div>

              {(!shop?.campaigns || shop?.campaigns.length === 0) ? (
                <div className="luxury-card p-12 text-center border-dashed border-2 border-black/5 bg-transparent">
                  <div className="w-16 h-16 bg-black/5 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Zap size={24} className="text-black/20" />
                  </div>
                  <h4 className="text-lg font-serif text-black mb-2">No Protocols Active</h4>
                  <p className="text-[10px] font-bold text-[#999] uppercase tracking-widest mb-8">Deploy your first scratch or stamp node to begin</p>
                  <button 
                    onClick={() => navigate('/admin/create-campaign')}
                    className="luxury-outline !px-8 !py-4 hover:bg-black hover:text-white transition-all"
                  >
                    Get Started
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {shop.campaigns.map((camp, i) => (
                    <div key={i} className="luxury-card p-10 border-black/[0.05] hover:border-[#d4af37]/20 transition-all group relative overflow-hidden">
                      <div className="flex justify-between items-start mb-8">
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${
                          camp.mode === 'stamp' ? 'bg-black text-[#d4af37]' : 'bg-black/5 text-black'
                        }`}>
                          {camp.mode === 'stamp' ? <Star size={24} /> : <Zap size={24} />}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`text-[8px] font-black uppercase tracking-widest px-3 py-1 rounded-full border ${
                            camp.mode === 'stamp' ? 'bg-[#d4af37]/10 border-[#d4af37]/20 text-[#d4af37]' : 'bg-black/5 border-black/10 text-black'
                          }`}>
                            {camp.mode === 'stamp' ? 'Stamp Protocol' : 'Scratch Protocol'}
                          </span>
                          {i === (shop.activeCampaignIdx || 0) && (
                            <span className="text-[7px] font-black uppercase tracking-[0.2em] text-emerald-600 flex items-center gap-1">
                              <div className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" /> Global Default
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <h4 className="text-2xl font-serif text-black mb-1">{camp.name}</h4>
                      <p className="text-[10px] font-bold text-[#999] uppercase tracking-widest mb-10">{camp.type}</p>
                      
                      {/* Embedded QR Node */}
                      <div className="flex flex-col items-center gap-8 mb-8 p-6 bg-black/[0.02] rounded-[2.5rem] border border-black/[0.05]">
                        <div className="p-4 bg-white rounded-2xl shadow-xl border border-black/5">
                          <QRCodeCanvas
                            id={`qr-node-${i}`}
                            value={`${customBaseUrl}/scan?shopId=${shop?.id}&campaignIdx=${i}`}
                            size={120}
                            level="H"
                          />
                        </div>
                        <div className="text-center">
                          <p className="text-[7px] font-black text-black/20 uppercase tracking-[0.4em] mb-2">Node URL</p>
                          <p className="text-[9px] font-mono opacity-40 truncate max-w-[150px]">
                            {`${customBaseUrl}/scan?shopId=${shop?.id}&campaignIdx=${i}`}
                          </p>
                        </div>
                      </div>

                      {/* Reward Preview Strip */}
                      <div className="flex items-center justify-between mb-8 px-2">
                        <div className="flex items-center gap-2">
                          {camp.rewards?.slice(0, 4).map((r, ri) => (
                            <div key={ri} className="w-10 h-10 rounded-xl bg-black/5 flex items-center justify-center text-lg shadow-sm border border-black/[0.03]" title={r.name}>
                              {r.icon}
                            </div>
                          ))}
                          {camp.rewards?.length > 4 && (
                            <span className="text-[10px] font-black text-[#999] ml-2">+{camp.rewards.length - 4}</span>
                          )}
                          {camp.mode === 'stamp' && (
                            <div className="flex items-center gap-2">
                               <div className="w-10 h-10 rounded-xl bg-black text-[#d4af37] flex items-center justify-center text-lg shadow-lg">
                                  {camp.finalReward?.icon || '🏆'}
                               </div>
                               <span className="text-[9px] font-bold text-black uppercase tracking-widest">Master Prize</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <button 
                          onClick={() => {
                            const canvas = document.getElementById(`qr-node-${i}`);
                            const pngUrl = canvas.toDataURL("image/png").replace("image/png", "image/octet-stream");
                            let downloadLink = document.createElement("a");
                            downloadLink.href = pngUrl;
                            downloadLink.download = `${camp.name}_QR.png`;
                            document.body.appendChild(downloadLink);
                            downloadLink.click();
                            document.body.removeChild(downloadLink);
                          }}
                          className="flex items-center justify-center gap-2 py-4 rounded-2xl bg-black text-[#d4af37] text-[9px] font-black uppercase tracking-widest hover:scale-[1.02] transition-all"
                        >
                          <Download size={14} /> Download QR
                        </button>
                        <button 
                          onClick={() => navigate(`/admin/create-campaign?idx=${i}`)}
                          className="flex items-center justify-center gap-2 py-4 rounded-2xl bg-white border border-black/10 text-[9px] font-black uppercase tracking-widest text-black hover:bg-black hover:text-white transition-all"
                        >
                          <Settings size={14} /> Configure
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Customer Activity Table */}
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-black uppercase tracking-widest">Recent Customer Activity</h3>
                <div className="flex items-center gap-2 text-[10px] font-bold text-[#999] uppercase tracking-widest">
                  <Users size={12} /> {scratches.length} Total Scratches
                </div>
              </div>

              <div className="luxury-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-black/[0.05] bg-black/[0.01]">
                        <th className="px-8 py-5 text-[9px] font-black uppercase tracking-widest text-[#999]">Customer Name</th>
                        <th className="px-8 py-5 text-[9px] font-black uppercase tracking-widest text-[#999]">Phone Number</th>
                        <th className="px-8 py-5 text-[9px] font-black uppercase tracking-widest text-[#999]">Loyalty Portfolio</th>
                        <th className="px-8 py-5 text-[9px] font-black uppercase tracking-widest text-[#999]">Last Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scratches.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="px-8 py-12 text-center text-[#ccc] text-[10px] font-bold uppercase tracking-widest">
                            No customers detected yet.
                          </td>
                        </tr>
                      ) : (
                        // Grouping by userPhone
                        Object.values(scratches.reduce((acc, curr) => {
                          const phone = curr.userPhone;
                          if (!acc[phone]) {
                            acc[phone] = {
                              userName: curr.userName,
                              userPhone: curr.userPhone,
                              scratches: 0,
                              stamps: 0,
                              lastTimestamp: curr.timestamp,
                              rewards: []
                            };
                          }
                          
                          // Categorize interaction accurately
                          if (curr.campaignMode) {
                            if (curr.campaignMode === 'stamp') acc[phone].stamps++;
                            else acc[phone].scratches++;
                          } else {
                            // Fallback for legacy data without campaignMode field
                            const campaign = shop?.campaigns?.find(c => c.name === curr.campaignName);
                            if (campaign?.mode === 'stamp') {
                              acc[phone].stamps++;
                            } else {
                              acc[phone].scratches++;
                            }
                          }
                          
                          // Update last active time if newer
                          if (curr.timestamp?.toDate() > acc[phone].lastTimestamp?.toDate()) {
                            acc[phone].lastTimestamp = curr.timestamp;
                          }
                          
                          return acc;
                        }, {}))
                        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                        .map((user, i) => (
                          <tr key={i} className="border-b border-black/[0.03] hover:bg-black/[0.01] transition-all">
                            <td className="px-8 py-6">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-black/5 flex items-center justify-center text-[10px] font-black text-black">
                                  {user.userName?.charAt(0) || 'U'}
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-sm font-bold text-black">{user.userName || 'Unknown User'}</span>
                                  <span className="text-[9px] font-bold text-emerald-600 uppercase tracking-widest">Verified Member</span>
                                </div>
                              </div>
                            </td>
                            <td className="px-8 py-6 text-xs font-medium text-[#666] tracking-wider">{user.userPhone}</td>
                            <td className="px-8 py-6">
                              <div className="flex gap-4">
                                <div className="flex flex-col">
                                  <span className="text-xl font-serif text-black">{user.scratches}</span>
                                  <span className="text-[8px] font-black text-[#999] uppercase tracking-widest">Scratches</span>
                                </div>
                                <div className="w-[1px] h-8 bg-black/5 self-center" />
                                <div className="flex flex-col">
                                  <span className="text-xl font-serif text-[#d4af37]">{user.stamps}</span>
                                  <span className="text-[8px] font-black text-[#999] uppercase tracking-widest">Stamps</span>
                                </div>
                              </div>
                            </td>
                            <td className="px-8 py-6">
                              <div className="flex flex-col">
                                <span className="text-[10px] font-bold text-black">{user.lastTimestamp?.toDate().toLocaleDateString()}</span>
                                <span className="text-[9px] text-[#999]">{user.lastTimestamp?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {scratches.length > itemsPerPage && (
                  <div className="px-8 py-6 bg-black/[0.01] border-t border-black/[0.05] flex items-center justify-between">
                    <p className="text-[10px] font-bold text-[#999] uppercase tracking-widest">
                      Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, scratches.length)} of {scratches.length} entries
                    </p>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className={`p-2 rounded-xl border transition-all ${currentPage === 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-black hover:text-white border-black/10'}`}
                      >
                        <ChevronLeft size={16} />
                      </button>
                      
                      <div className="flex items-center gap-1">
                        {[...Array(Math.ceil(scratches.length / itemsPerPage))].map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentPage(idx + 1)}
                            className={`w-8 h-8 rounded-xl text-[10px] font-black transition-all ${currentPage === idx + 1 ? 'bg-black text-[#d4af37]' : 'text-[#999] hover:bg-black/5'}`}
                          >
                            {idx + 1}
                          </button>
                        ))}
                      </div>

                      <button 
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(scratches.length / itemsPerPage)))}
                        disabled={currentPage === Math.ceil(scratches.length / itemsPerPage)}
                        className={`p-2 rounded-xl border transition-all ${currentPage === Math.ceil(scratches.length / itemsPerPage) ? 'opacity-30 cursor-not-allowed' : 'hover:bg-black hover:text-white border-black/10'}`}
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar - Node Settings */}
          <div className="lg:col-span-4">
            <div className="luxury-card p-10 sticky top-12 border-black/[0.05] space-y-10">
              <div>
                <h3 className="text-sm font-bold text-black uppercase tracking-widest mb-6">Network Configuration</h3>
                <div className="p-6 bg-black/5 rounded-2xl border border-black/[0.03] space-y-4">
                  <div>
                    <p className="text-[10px] font-black text-black/40 uppercase tracking-widest mb-3">Base Deployment URL</p>
                    <input 
                      type="text" 
                      value={customBaseUrl}
                      onChange={(e) => setCustomBaseUrl(e.target.value)}
                      placeholder="http://192.168.x.x:3000"
                      className="w-full bg-white border border-black/[0.05] rounded-xl py-4 px-6 text-xs text-black focus:outline-none focus:border-[#d4af37]/50 transition-all font-mono"
                    />
                    <p className="text-[8px] text-[#999] mt-3 leading-relaxed uppercase font-bold tracking-widest">
                      Set this to your current server address to generate functional QR codes.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-black uppercase tracking-widest mb-6">Operational Insights</h3>
                <div className="space-y-4">
                  <InsightRow label="Network Node" value="Active" color="emerald" />
                  <InsightRow label="Data Sync" value="Real-time" color="emerald" />
                  <InsightRow label="Campaigns" value={shop?.campaigns?.length || 0} color="black" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center opacity-20">
          <p className="text-[8px] font-black tracking-widest text-black uppercase">System Node Synced: {new Date().toLocaleTimeString()}</p>
        </div>
      </main>
    </div>
  );
};

const InsightRow = ({ label, value, color }) => (
  <div className="flex items-center justify-between p-4 bg-black/[0.02] rounded-xl border border-black/[0.03]">
    <span className="text-[10px] font-bold text-[#999] uppercase tracking-widest">{label}</span>
    <span className={`text-[10px] font-black uppercase tracking-widest text-${color}-600`}>{value}</span>
  </div>
);

const SidebarIcon = ({ icon, active, onClick }) => (
  <button 
    onClick={onClick}
    className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 ${
    active ? 'bg-black text-[#d4af37] shadow-xl' : 'text-[#ccc] hover:text-black hover:bg-black/5'
  }`}>
    {icon}
  </button>
);

const StatCard = ({ title, value, change, isUp, icon }) => (
  <div className="luxury-card p-8 group hover:border-[#d4af37]/30 transition-all duration-500 overflow-hidden relative border-black/[0.05]">
    <div className="flex justify-between items-start mb-10 relative z-10">
      <div className="w-14 h-14 rounded-2xl bg-black/5 flex items-center justify-center text-black group-hover:bg-black group-hover:text-[#d4af37] transition-all shadow-sm">
        {icon}
      </div>
      <div className={`flex items-center gap-1 text-[10px] font-black ${isUp ? 'text-emerald-600' : 'text-rose-600'} bg-black/[0.03] px-3 py-1 rounded-full`}>
        {isUp ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
        {change}
      </div>
    </div>
    <div className="relative z-10">
      <p className="text-[#999] text-[10px] font-bold uppercase tracking-widest mb-2">{title}</p>
      <h2 className="text-3xl font-serif text-black tracking-tight">{value}</h2>
    </div>
  </div>
);

export default Dashboard;
